import { i18nextT } from "hooks/i18n/i18n";
import videojs from "video.js";

import {
  createTheoPictureInPictureController,
  createVideoElementPictureInPictureController,
} from "./pictureInPicture";
import { getVideoJsComponent } from "./utils";

import type {
  PictureInPictureController,
  TheoPlayerForPictureInPicture,
} from "./pictureInPicture";
import type { THEOPlayerRuntime } from "./theoplayerRuntime";
import type Player from "video.js/dist/types/player";

/**
 * Picture-in-Picture 中にプレイヤー要素へ付与するクラス。
 * オーバーレイの表示と、その上にコントロールバーを出す重なり順の切り替えを CSS 側で行う。
 */
const PICTURE_IN_PICTURE_ACTIVE_CLASS = "vjs-custom-picture-in-picture-active";

/**
 * Figma「PiP icon 64」(2600:1512) と同じ形状。
 * 外枠 46x32 (線幅 5) の中に 21x14 の小窓、いずれも白の 55%。
 */
const pictureInPictureIcon = `
      <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect x="11.5" y="18.5" width="41" height="27" stroke="white" stroke-opacity="0.55" stroke-width="5"/>
        <rect x="29" y="29" width="21" height="14" fill="white" fill-opacity="0.55"/>
      </svg>
    `;

type GetPictureInPictureOverlayArgs =
  | {
      playerType: "THEOPlayer";
      runtime: THEOPlayerRuntime;
      theoPlayer: TheoPlayerForPictureInPicture;
    }
  | { playerType: "IVS" };

/**
 * ネイティブ Picture-in-Picture 中、元のプレイヤー位置には
 * ブラウザ組み込みの「ピクチャー イン ピクチャー モードで再生しています」が
 * video 要素の内部に描画される。これは CSS / JS では変更できないため、
 * 上から不透明なオーバーレイを重ねて自前の表示 (アイコン・文言) に差し替える。
 * PiP の解除はコントロールバーの PiP ボタンか小窓側の操作で行う。
 */
export const getPictureInPictureOverlay = (
  args: GetPictureInPictureOverlayArgs
) => {
  const VideoJSComponent =
    args.playerType === "IVS"
      ? videojs.getComponent("Component")
      : getVideoJsComponent("Component", args.runtime);

  class PictureInPictureOverlay extends VideoJSComponent {
    private readonly controller: PictureInPictureController;

    constructor(player: Player, options = {}) {
      super(player, options);

      this.controller =
        args.playerType === "THEOPlayer"
          ? createTheoPictureInPictureController(args.theoPlayer)
          : createVideoElementPictureInPictureController(player.el());

      const unsubscribe = this.controller.subscribe(this.syncActiveClass);
      this.on("dispose", () => {
        unsubscribe();
        player.removeClass(PICTURE_IN_PICTURE_ACTIVE_CLASS);
      });

      this.syncActiveClass();
    }

    syncActiveClass = () => {
      if (this.controller.isActive()) {
        this.player().addClass(PICTURE_IN_PICTURE_ACTIVE_CLASS);
      } else {
        this.player().removeClass(PICTURE_IN_PICTURE_ACTIVE_CLASS);
      }
    };

    createEl() {
      const message = i18nextT("streaming.controller.playingInMiniPlayer");
      return super.createEl("div", {
        className: "vjs-custom-picture-in-picture-overlay",
        innerHTML: `
          <div class="vjs-custom-picture-in-picture-overlay-content">
            ${pictureInPictureIcon}
            <p class="vjs-custom-picture-in-picture-overlay-message">${message}</p>
          </div>
        `,
      });
    }
  }

  return PictureInPictureOverlay;
};
